import React, { useEffect, useState } from 'react';
import { Alert, Button, Switch } from 'antd';
import { BellOutlined, CheckCircleOutlined } from '@ant-design/icons';
import { usePush } from './PushProvider';
import { Trans, useTranslation } from 'react-i18next';
import styles from './PushSettings.module.scss';

type Props = { compact?: boolean; context?: 'home' | 'league' | 'match' };

export default function PushSettings({ compact = false, context }: Props) {
  const push = usePush();
  const { t } = useTranslation();
  const Title = compact ? 'p' : 'h3';
  const [dismissal, setDismissal] = useState<{ uid: string | null; until: number }>({ uid: null, until: 0 });
  const [showGuide, setShowGuide] = useState(false);
  useEffect(() => {
    setShowGuide(false);
    const refreshDismissal = () => {
      let until = 0;
      try { until = Number(localStorage.getItem(`push-later:${push.uid}`)) || 0; }
      catch { /* Storage is optional; the prompt remains dismissible for this visit. */ }
      setDismissal({ uid: push.uid, until });
    };
    refreshDismissal();
    window.addEventListener('storage', refreshDismissal);
    return () => window.removeEventListener('storage', refreshDismissal);
  }, [push.uid]);
  if (!push.uid) return null;
  const dismissed = dismissal.uid === push.uid && dismissal.until > Date.now();
  if (compact && (dismissal.uid !== push.uid || !push.available || dismissed ||
    (push.busy && !push.ready) || (push.enabled && push.subscribed) ||
    (!push.supported && !push.needsInstall))) return null;
  const dismiss = () => {
    const until = Date.now() + 3 * 86400000;
    setDismissal({ uid: push.uid, until });
    try { localStorage.setItem(`push-later:${push.uid}`, String(until)); } catch { /* optional */ }
  };
  return (
    <section className={styles.card} aria-label={t('notifications.push.title')}>
      <div className={styles.heading}>
        <span className={styles.icon} aria-hidden="true"><BellOutlined /></span>
        <div>
          <Title className={styles.title} data-push-title>{compact ? t(`notifications.push.${context ? `prompt.${context}.title` : 'compactTitle'}`) : t('notifications.push.title')}</Title>
          <p>{t(compact && context ? `notifications.push.prompt.${context}.description` : 'notifications.push.intro')}</p>
        </div>
      </div>
      <div role="status" aria-live="polite">
        {push.busy && !push.ready && <p>{t('notifications.push.checking')}</p>}
        {!push.busy && !push.available && !push.error && <p>{t('notifications.push.unavailable')}</p>}
        {push.available && push.enabled && push.subscribed && <p className={styles.success}><CheckCircleOutlined /> {t('notifications.push.enabledHere')}</p>}
        {push.available && push.enabled && !push.subscribed && <p>{t('notifications.push.needsDevice')}</p>}
        {push.available && !push.needsInstall && !push.supported && <p>{t('notifications.push.unsupported')}</p>}
      </div>
      {push.error && <Alert type="warning" showIcon message={push.error} />}
      {push.available && push.denied && !push.needsInstall && <>
        <p>{t('notifications.push.denied')}</p>
        <Button className={styles.guideButton} onClick={() => setShowGuide(!showGuide)} aria-expanded={showGuide}>
          {t('notifications.push.howToUnblock')}
        </Button>
        {showGuide && <ol className={styles.guide}>
          <li>{t('notifications.push.unblockBrowser')}</li>
          <li>{t('notifications.push.unblockPhone')}</li>
          <li>{t('notifications.push.unblockReturn')}</li>
        </ol>}
      </>}
      {push.available && push.needsInstall && <>
        <p>{t('notifications.push.needsInstall')}</p>
        <Button className={styles.guideButton} onClick={() => setShowGuide(!showGuide)} aria-expanded={showGuide}>{t('notifications.push.howToInstall')}</Button>
        {showGuide && <ol className={styles.guide}>
          <li><Trans i18nKey="notifications.push.guide1" components={{ b: <strong /> }} /></li>
          <li><Trans i18nKey="notifications.push.guide2" components={{ b: <strong /> }} /></li>
          <li>{t('notifications.push.guide3')}</li>
          <li><Trans i18nKey="notifications.push.guide4" components={{ b: <strong /> }} /></li>
        </ol>}
      </>}
      {compact ? <div className={styles.actions}>
        {push.available && push.supported && !push.denied && !push.needsInstall && (!push.enabled || !push.subscribed) &&
          <Button loading={push.busy} disabled={!push.ready} onClick={push.enable}>{t('notifications.push.enable')}</Button>}
        {push.available && push.supported && !push.needsInstall && !push.ready && !push.busy &&
          <p className={styles.note}>{t('notifications.push.reloadToRetry')}</p>}
        <Button type="text" onClick={dismiss}>{t('notifications.push.later')}</Button>
      </div> : <div className={styles.reminders}>
        <div>
          <strong id="push-notifications-toggle-label">{t('notifications.push.toggle')}</strong>
          <p id="push-notifications-toggle-description">{push.enabled && !push.subscribed
            ? t('notifications.push.needsDevice') : t('notifications.push.footnote')}</p>
        </div>
        <Switch aria-labelledby="push-notifications-toggle-label"
          aria-describedby="push-notifications-toggle-description"
          checked={push.enabled && push.subscribed}
          disabled={!push.ready || push.busy || !push.available || !push.supported || push.needsInstall || push.denied}
          loading={push.busy && push.ready}
          onChange={enabled => (enabled ? push.enable() : push.disable())} />
      </div>}
      {!compact && (push.engagementAvailable || push.engagementEnabled) && <div className={styles.reminders}>
        <div>
          <strong id="engagement-reminders-label">{t('notifications.push.remindersTitle')}</strong>
          <p id="engagement-reminders-description">{t('notifications.push.remindersDescription')}</p>
          {!push.enabled && <p className={styles.note}>{t('notifications.push.enableFirst')}</p>}
        </div>
        <Switch id="engagement-reminders" aria-labelledby="engagement-reminders-label"
          aria-describedby="engagement-reminders-description" checked={push.engagementEnabled}
          disabled={push.busy || !push.enabled || (!push.engagementAvailable && !push.engagementEnabled)}
          loading={push.busy && push.ready} onChange={push.setEngagement} />
      </div>}
    </section>
  );
}

PushSettings.defaultProps = { compact: false, context: undefined };
